import { Controller } from "@hotwired/stimulus";

const CATEGORIES_BY_TYPE = {
  income: [
    ["Dividends", "dividends"],
    ["Rental Income", "rental_income"],
    ["Interest", "interest"],
    ["Sale Proceeds", "sale_proceeds"],
    ["Insurance Payout", "insurance_payout"],
    ["Refund", "refund"],
    ["Other Income", "other_income"],
  ],
  expense: [
    ["Funeral & Burial", "funeral"],
    ["Legal Fees", "legal_fees"],
    ["Accounting Fees", "accounting_fees"],
    ["Appraisal Fees", "appraisal_fees"],
    ["Taxes", "taxes"],
    ["Mortgage / Debt", "mortgage"],
    ["Utilities", "utilities"],
    ["Property Maintenance", "property_maintenance"],
    ["Insurance", "insurance"],
    ["Administrative", "administrative"],
    ["Other Expense", "other_expense"],
  ],
  time_log: [
    ["Administration", "administration"],
    ["Communication", "communication"],
    ["Travel", "travel"],
    ["Meetings", "meetings"],
    ["Document Preparation", "document_prep"],
    ["Court Appearance", "court_appearance"],
    ["Other", "other_time"],
  ],
  mileage_log: [
    ["Estate Business", "estate_business"],
    ["Court Visit", "court_visit"],
    ["Property Visit", "property_visit"],
    ["Errand", "errand"],
    ["Other", "other_mileage"],
  ],
};

const FIELD_TARGETS = {
  income: null,
  expense: null,
  time_log: "timeLogFields",
  mileage_log: "mileageLogFields",
};

const COMPUTED_TYPES = ["time_log", "mileage_log"];

export default class extends Controller {
  static targets = [
    "typeSelect",
    "categorySelect",
    "timeLogFields",
    "mileageLogFields",
    "includeAsExpenseField",
    "amountInput",
    "amountRequiredStar",
    "durationHours",
    "durationMinutes",
    "durationCombined",
    "startOdometer",
    "endOdometer",
    "vehicleLabel",
    "hourlyRateInput",
    "mileageRateInput",
    "payerField",
    "payeeField",
    "payerInput",
    "payeeInput",
  ];

  connect() {
    this.update();
  }

  update() {
    const type = this.typeSelectTarget.value;
    this.toggleFields(type);
    this.toggleCounterparty(type);
    this.toggleIncludeAsExpense(type);
    this.swapCategories(type);
    this.clearInactiveFields(type);
    this.toggleAmountReadonly(type);
    this.recalc();
  }

  toggleIncludeAsExpense(type) {
    if (!this.hasIncludeAsExpenseFieldTarget) return;
    this.includeAsExpenseFieldTarget.hidden = !COMPUTED_TYPES.includes(type);
  }

  toggleCounterparty(type) {
    const showPayer = type === "income";
    if (this.hasPayerFieldTarget) this.payerFieldTarget.hidden = !showPayer;
    if (this.hasPayeeFieldTarget) this.payeeFieldTarget.hidden = showPayer;
  }

  toggleFields(type) {
    Object.entries(FIELD_TARGETS).forEach(([key, targetName]) => {
      if (!targetName) return;
      const targetProp = `has${this.cap(targetName)}Target`;
      if (!this[targetProp]) return;
      this[`${targetName}Target`].hidden = key !== type;
    });
  }

  swapCategories(type) {
    if (!this.hasCategorySelectTarget) return;
    const options = CATEGORIES_BY_TYPE[type] || [];
    const current = this.categorySelectTarget.value;
    const html =
      '<option value="">Select category</option>' +
      options
        .map(
          ([label, value]) =>
            `<option value="${value}"${value === current ? " selected" : ""}>${label}</option>`
        )
        .join("");
    this.categorySelectTarget.innerHTML = html;
  }

  clearInactiveFields(type) {
    if (type !== "time_log") {
      this.clearValue("durationHours");
      this.clearValue("durationMinutes");
      this.clearValue("durationCombined");
    }
    if (type !== "mileage_log") {
      this.clearValue("startOdometer");
      this.clearValue("endOdometer");
      this.clearValue("vehicleLabel");
    }
    if (type === "income") {
      this.clearValue("payeeInput");
    } else {
      this.clearValue("payerInput");
    }
  }

  toggleAmountReadonly(type) {
    if (!this.hasAmountInputTarget) return;
    const computed = COMPUTED_TYPES.includes(type);
    this.amountInputTarget.readOnly = computed;
    if (computed) {
      this.amountInputTarget.classList.add("amount-computed");
    } else {
      this.amountInputTarget.classList.remove("amount-computed");
    }
    if (this.hasAmountRequiredStarTarget) {
      this.amountRequiredStarTarget.hidden = computed;
    }
  }

  recalc() {
    const type = this.typeSelectTarget.value;
    if (type === "time_log") this.recalcTime();
    else if (type === "mileage_log") this.recalcMileage();
  }

  recalcTime() {
    const minutes = this.totalDurationMinutes();
    if (this.hasDurationCombinedTarget) {
      this.durationCombinedTarget.value = minutes || "";
    }
    const amount = (minutes / 60) * this.hourlyRate();
    this.writeAmount(amount);
  }

  recalcMileage() {
    const start = this.intValue("startOdometer");
    const end = this.intValue("endOdometer");
    const miles = Math.max(end - start, 0);
    const amount = miles * this.mileageRate();
    this.writeAmount(amount);
  }

  hourlyRate() {
    if (!this.hasHourlyRateInputTarget) return 0;
    return parseFloat(this.hourlyRateInputTarget.value) || 0;
  }

  mileageRate() {
    if (!this.hasMileageRateInputTarget) return 0;
    return parseFloat(this.mileageRateInputTarget.value) || 0;
  }

  writeAmount(amount) {
    if (!this.hasAmountInputTarget) return;
    this.amountInputTarget.value = amount > 0 ? amount.toFixed(2) : "";
  }

  totalDurationMinutes() {
    const hours = this.intValue("durationHours");
    const mins = this.intValue("durationMinutes");
    return hours * 60 + mins;
  }

  intValue(name) {
    const targetProp = `has${this.cap(name)}Target`;
    if (!this[targetProp]) return 0;
    return parseInt(this[`${name}Target`].value, 10) || 0;
  }

  clearValue(name) {
    const targetProp = `has${this.cap(name)}Target`;
    if (!this[targetProp]) return;
    this[`${name}Target`].value = "";
  }

  cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
}
